import { useEffect, useRef } from 'react';
import type { CustomerOrderDetail } from '@capsico/contracts';
import { useCountdown } from './useCountdown';
import { usePayment, type PaymentMessages } from './usePayment';
import { useInvoiceDownload, useRefundChoice } from './useRefunds';

/**
 * Everything about money on the order page (W11): the waiting-for-payment state (countdown to the
 * attempt's expiry, resume, another way, cancel), refunds with the wallet-or-source choice, and
 * tax invoice downloads. Right after checkout (`autoStart`) the gateway opens once by itself.
 */
export function useOrderPayment(
  order: CustomerOrderDetail | null,
  options: { autoStart: boolean; onAutoStarted: () => void },
  messages: PaymentMessages & { refundSaved: string; invoiceFailed: string },
) {
  const orderId = order?.id ?? '';
  const pay = usePayment(orderId, messages);
  const intent = order?.payment.intent ?? null;
  const waiting = order?.stage === 'awaiting_payment';
  const pendingIntent = waiting && intent?.status === 'pending' ? intent : null;
  const countdown = useCountdown(pendingIntent?.expiresAt ?? null);
  const refunds = useRefundChoice({ orderId }, { saved: messages.refundSaved });
  const invoices = useInvoiceDownload(orderId, { failed: messages.invoiceFailed });

  // Open the gateway once, straight after "Pay" on checkout.
  const autoStarted = useRef(false);
  const { autoStart, onAutoStarted } = options;
  const { start } = pay;
  useEffect(() => {
    if (!autoStart || autoStarted.current || !order) return;
    autoStarted.current = true;
    onAutoStarted();
    if (pendingIntent?.session) start(pendingIntent);
  }, [autoStart, order, pendingIntent, start, onAutoStarted]);

  return {
    ...pay,
    waiting,
    intent: pendingIntent,
    canResume: pendingIntent !== null && pendingIntent.session !== null && !countdown.expired,
    canSwitch: waiting && (order?.payment.retryMethods.length ?? 0) > 0,
    timer: pendingIntent ? countdown.text : null,
    expired: pendingIntent !== null && countdown.expired,
    resume: () => {
      if (pendingIntent) pay.start(pendingIntent);
    },
    chooseRefund: refunds.choose,
    choosingRefundId: refunds.choosingId,
    downloadInvoice: invoices.download,
    downloadingInvoice: invoices.downloading,
  };
}
